/**
 * Module 8 — the app shell.
 *
 * Owns which screen you're on and every piece of flow state the screens share,
 * and nothing else. All the domain logic lives in `features/`; all the layout
 * lives in the screens.
 *
 * Storage is the single source of truth: every write goes through `write()` and
 * is followed by a re-read, so the screen can never drift from what's stored.
 */

import { useEffect, useState } from "react";

import { CategoryMenu } from "./components/CategoryMenu";
import { CookSheet } from "./components/CookSheet";
import { Dock } from "./components/Dock";
import { withViewTransition } from "./lib/motion/viewTransition";
import { EditItemSheet } from "./components/EditItemSheet";
import { Icon } from "./components/Icon";
import type { Tab } from "./components/NavBar";
import { CategoryPicker } from "./components/CategoryPicker";
import { KitchenScreen } from "./components/KitchenScreen";
import type { KitchenView } from "./components/KitchenScreen";
import { CartScreen } from "./components/CartScreen";
import { loadCart, saveCart } from "./features/cart/cartStorage";
import { lineToItem } from "./features/cart/stow";
import { suggestCart } from "./features/cart/suggestCart";
import type { CartLine } from "./features/cart/types";
import { suggestRecipes } from "./features/recipes/suggestRecipes";
import { RECIPES } from "./features/recipes/recipes";
import { useSettings } from "./features/settings/SettingsContext";
import { LearnScreen } from "./components/LearnScreen";
import { TechniqueSheet } from "./components/TechniqueSheet";
import { QuickAddItem } from "./components/QuickAddItem";
import { ScanReview } from "./components/ScanReview";
import type { ReviewRow } from "./components/ScanReview";
import { ReceiptReview } from "./components/ReceiptReview";
import type { ReceiptRow } from "./components/ReceiptReview";
import type { ReceiptRead } from "./features/receipts/types";
import { readReceipt } from "./features/receipts/readReceipt";
import { DOCUMENT_BUDGETS, downscaleDocument } from "./lib/image/downscalePhoto";
import { RecipesScreen } from "./components/RecipesScreen";
import { SettingsScreen } from "./components/SettingsScreen";
import { Toast } from "./components/Toast";
import type { CommonItem } from "./features/food/commonItems";
import type { FoodCategory, FoodItem, StorageLocation } from "./features/food/types";
import { useTheme } from "./features/settings/useTheme";
import { addDays, todayIso } from "./lib/date/isoDate";
import type { Recognition } from "./features/vision/types";
import { CATEGORY_RULES } from "./features/food/categories";
import { applyCook, describeCook } from "./features/recipes/cooked";
import { loadPractice } from "./features/techniques/practiceStorage";
import type { PracticeLog } from "./features/techniques/practiceStorage";
import type { Technique } from "./features/techniques/types";
import type { CookChoice } from "./features/recipes/cooked";
import {
  createId,
  deleteFoodItem,
  loadFoodItems,
  replaceFoodItems,
  reportPersistErrorsTo,
  saveFoodItem,
} from "./lib/storage/foodStorage";

import "./App.css";

/** What the add sheet is working on, or null when it's closed. */
interface Adding {
  category: FoodCategory;
  /** A common item to start from, or null for a blank custom item. */
  preset: CommonItem | null;
  /** Set when the sheet was opened straight from the camera. */
  photo?: string;
}

/**
 * Where a photographed item starts before anything is known about it. The
 * catch-all, so recognition is free to move it anywhere; without recognition
 * it's still the honest answer until the user says otherwise.
 */
const CAMERA_CATEGORY: FoodCategory = "other";

/** How long a deleted item can be brought back. */
const UNDO_MS = 5000;

const TAB_TITLES: Record<Tab, string> = {
  kitchen: "Kitchen",
  recipes: "Recipes",
  learn: "Learn",
  settings: "Settings",
};

export default function App() {
  useTheme();
  const { settings } = useSettings();

  const [tab, setTab] = useState<Tab>("kitchen");
  const [items, setItems] = useState<FoodItem[]>([]);
  /** Which appliance is open on the kitchen screen. */
  const [openPlace, setOpenPlace] = useState<StorageLocation | null>(null);
  /** In the house, or to buy. */
  const [kitchenView, setKitchenView] = useState<KitchenView>("have");
  /* Owned here rather than by the screen, so switching tabs and coming back
     doesn't silently throw away what someone typed. */
  const [query, setQuery] = useState("");
  const [cart, setCart] = useState<CartLine[]>([]);
  /** True while the "which category?" sheet is up, on the way to adding. */
  const [picking, setPicking] = useState(false);
  /** A photo that turned out to hold several things, waiting to be reviewed. */
  const [scanned, setScanned] = useState<{ found: Recognition[]; photo: string } | null>(null);
  /* No photo travels with this, and that is the point — see ReceiptReview. */
  const [receipt, setReceipt] = useState<ReceiptRead | null>(null);
  const [receiptStatus, setReceiptStatus] = useState<"idle" | "working">("idle");
  /**
   * A just-stowed batch, held in memory only until the toast expires.
   *
   * The whole previous inventory is kept rather than a list of ids, for the
   * same reason cooking keeps one: undoing has to put things back exactly as
   * they were, and reconstructing that from a diff is a second chance to get it
   * wrong.
   */
  const [stowedBatch, setStowed] = useState<{
    items: FoodItem[];
    lines: CartLine[];
    before: FoodItem[];
  } | null>(null);
  /** The technique being read, and how much practice each has had. */
  const [technique, setTechnique] = useState<Technique | null>(null);
  const [practice, setPractice] = useState<PracticeLog>({});
  const [menuCategory, setMenuCategory] = useState<FoodCategory | null>(null);
  const [adding, setAdding] = useState<Adding | null>(null);
  const [editing, setEditing] = useState<FoodItem | null>(null);
  /** A just-deleted item, held in memory only until the toast expires. */
  const [undoable, setUndoable] = useState<FoodItem | null>(null);
  /** A recipe waiting to be told what it used up. */
  const [cooking, setCooking] = useState<{ title: string; used: FoodItem[] } | null>(null);
  /**
   * What to say and what to restore after cooking. The whole previous
   * inventory is kept rather than a diff: cooking can remove several items and
   * reduce several more, and putting that back one operation at a time would
   * be a second chance to get it wrong.
   */
  const [cooked, setCooked] = useState<{ message: string; before: FoodItem[] } | null>(null);
  const [error, setError] = useState<string | null>(null);

  /*
   * A write that fails now fails *after* the call returns.
   *
   * Inventory writes go to IndexedDB in the background, so a failure has no
   * call site left to throw into. It is reported here instead and shown as a
   * page-level error — less precise than the old synchronous StorageError, and
   * the trade for a quota that is no longer a routine failure.
   */
  useEffect(() => {
    reportPersistErrorsTo(setError);
  }, []);

  // Storage is only readable in the browser, so the first read happens after mount.
  useEffect(() => {
    setItems(loadFoodItems());
    setPractice(loadPractice());
    setCart(loadCart());
  }, []);

  /** Runs a storage write, refreshes from storage either way, reports failures. */
  function write(action: () => void) {
    setError(null);
    let ok = true;
    try {
      action();
    } catch (writeError) {
      ok = false;
      setError(writeError instanceof Error ? writeError.message : "Couldn't save that.");
    }
    // Re-read even on failure, so the list shows what is really stored rather
    // than a change that didn't happen.
    setItems(loadFoodItems());
    return ok;
  }

  function handleAdd(item: FoodItem) {
    // The sheet stays open on failure, so the typing isn't lost.
    if (write(() => saveFoodItem(item))) setAdding(null);
  }

  /**
   * Writes everything kept from a shelf scan, in one go.
   *
   * The photo goes on every item. That is a few duplicated thumbnails — around
   * 12KB each against a 5MB budget — and worth it: seeing which shelf a row
   * came from is most of what makes a bulk scan reviewable afterwards.
   */
  function confirmScan(rows: ReviewRow[], photo: string) {
    const purchaseDate = todayIso();
    const items: FoodItem[] = rows.map((row) => ({
      id: createId(),
      name: row.name.trim(),
      category: row.category,
      location: CATEGORY_RULES[row.category].defaultLocation,
      quantity: 1,
      unit: CATEGORY_RULES[row.category].defaultUnit,
      purchaseDate,
      estimatedExpiryDate: addDays(purchaseDate, row.shelfLifeDays),
      photo,
    }));

    setScanned(null);
    // One write rather than one per item: a shelf of eight would otherwise
    // re-read and re-serialise the whole inventory eight times.
    write(() => replaceFoodItems([...loadFoodItems(), ...items]));
  }

  /**
   * A whole shop, written in one go.
   *
   * Two differences from `confirmScan`, both because a receipt states things a
   * photograph can't: the quantity and unit come off the line rather than
   * defaulting, and every item is dated from the receipt rather than from
   * today. That second one is the entire reason this feature and the
   * purchase-date field arrived together — a shop dated today has its whole
   * best-before schedule wrong from the first day.
   *
   * No photo is attached. A receipt image is 400KB and has no use once it has
   * been read; keeping one would also put the shop, the time and a card's last
   * four digits into localStorage and into the next backup export.
   */
  function confirmReceipt(rows: ReceiptRow[], purchaseDate: string) {
    const items: FoodItem[] = rows.map((row) => ({
      id: createId(),
      name: row.name.trim(),
      category: row.category,
      location: CATEGORY_RULES[row.category].defaultLocation,
      quantity: Number(row.quantity),
      unit: row.unit,
      purchaseDate,
      estimatedExpiryDate: addDays(purchaseDate, row.shelfLifeDays),
    }));

    setReceipt(null);
    write(() => replaceFoodItems([...loadFoodItems(), ...items]));
  }

  /**
   * Reads one photo of a receipt and opens the review sheet on what came back.
   *
   * The photo is a local `const` and nothing else ever sees it: it goes to the
   * proxy, the lines come back, and it is dropped when this function returns.
   * That is the whole privacy design and it works because nothing stores it —
   * not React state, not the recognition cache, not the item.
   */
  async function scanReceipt(file: File) {
    setPicking(false);
    setReceiptStatus("working");
    setError(null);

    /*
     * Try smaller before giving up.
     *
     * A receipt refused at 150KB should come back at 90KB rather than not at
     * all — the smaller read is slightly worse, and "slightly worse" beats "the
     * feature does not work". Only failures that shrinking could plausibly fix
     * are retried: a rejected key or an empty account refuses every size just
     * as fast, and hammering it three times is rude to an allowance that is
     * already spent.
     */
    const worthShrinking = new Set(["timeout", "server-error", "unreachable"]);
    let last: Awaited<ReturnType<typeof readReceipt>> | null = null;

    for (const budget of DOCUMENT_BUDGETS) {
      let photo: string;
      try {
        photo = await downscaleDocument(file, budget);
      } catch (photoError) {
        setReceiptStatus("idle");
        setError(
          photoError instanceof Error ? photoError.message : "That photo couldn't be used."
        );
        return;
      }

      const outcome = await readReceipt(photo, todayIso());
      last = outcome;

      if (outcome.status === "ok") {
        setReceiptStatus("idle");
        setReceipt(outcome.read);
        return;
      }
      if (outcome.status === "off" || !worthShrinking.has(outcome.reason)) break;
    }

    setReceiptStatus("idle");
    setError(
      last && last.status === "failed"
        ? last.message
        : "Photo recognition is switched off in this build."
    );
  }

  /**
   * The shopping list writes through storage like everything else, so a
   * refresh mid-shop doesn't lose what you ticked.
   */
  function writeCart(next: CartLine[]) {
    if (write(() => saveCart(next))) setCart(loadCart());
  }

  function addToCart(name: string, source: CartLine["source"], reason?: string) {
    // Same name twice is the one thing a list must not do — it's suggested by
    // two recipes and typed in by hand, and three "chorizo" lines is a list
    // nobody trusts.
    if (cart.some((line) => line.name.toLowerCase() === name.trim().toLowerCase())) return;
    writeCart([...cart, { id: createId(), name: name.trim(), source, reason, got: false }]);
  }

  /**
   * Everything ticked off in the shop, filed as food in the house.
   *
   * One write for the items and one for the list, then a single toast that can
   * take back **both** — putting the lines back on the list and the items back
   * out of the inventory. An undo that only reversed half of it would leave the
   * two disagreeing, which is worse than not offering one.
   *
   * The category is a guess (see `stow.ts`), so this deliberately lands in the
   * inventory rather than in a review sheet: these are things the user
   * personally put on a list and personally ticked off, which is a much
   * stronger claim than a model reading a shelf. The undo is the safety net.
   */
  function stowCheckedItems() {
    const ticked = cart.filter((line) => line.got);
    if (ticked.length === 0) return;

    const before = loadFoodItems();
    const stowed = ticked.map((line) => lineToItem(line, createId(), today));
    const remaining = cart.filter((line) => !line.got);

    setStowed({ items: stowed, lines: ticked, before });
    write(() => replaceFoodItems([...before, ...stowed]));
    writeCart(remaining);
  }

  function undoStow() {
    if (!stowedBatch) return;
    const { lines, before } = stowedBatch;
    setStowed(null);
    write(() => replaceFoodItems(before));
    writeCart([...loadCart(), ...lines]);
  }

  function handleSave(item: FoodItem) {
    if (write(() => saveFoodItem(item))) setEditing(null);
  }

  function handleDelete(id: string) {
    const removed = items.find((item) => item.id === id);
    if (!removed) return;

    if (write(() => deleteFoodItem(id))) {
      setEditing(null);
      // Held in memory rather than left in storage: what's on screen is always
      // exactly what's stored, and an abandoned undo can't resurrect anything.
      setUndoable(removed);
    }
  }

  function confirmCook(choices: CookChoice[]) {
    const before = items;
    const outcome = applyCook(before, choices);
    const message = describeCook(outcome);

    setCooking(null);
    // Nothing ticked and nothing countable to reduce: no reason to write.
    if (!message) return;

    if (write(() => replaceFoodItems(outcome.items))) {
      setCooked({ message, before });
    }
  }

  function undoCook() {
    if (!cooked) return;
    const { before } = cooked;
    setCooked(null);
    write(() => replaceFoodItems(before));
  }

  function undoDelete() {
    if (!undoable) return;
    const restored = undoable;
    setUndoable(null);
    write(() => saveFoodItem(restored));
  }

  // Read once per render so every row counts from the same day.
  const today = todayIso();

  /*
   * What the list is offering, derived rather than stored.
   *
   * Recomputed from the same ranked suggestions the recipes screen shows, so
   * the gaps arrive in the order that screen would put them in — what's about
   * to spoil first. Deriving rather than storing means it can never go stale
   * against an inventory that just changed.
   */
  const cartSuggestions = suggestCart(
    suggestRecipes(RECIPES, items, settings, today),
    items,
    cart
  );
  const anySheetOpen = Boolean(
    adding || editing || menuCategory || cooking || picking || scanned || receipt || technique
  );

  return (
    <>
      <main className="app">
        <h1 className="app__title">{TAB_TITLES[tab]}</h1>

        {error && (
          <p className="app__error" role="alert">
            <Icon name="alert" className="app__error-icon" />
            {error}
          </p>
        )}

        {tab === "kitchen" && (
          <KitchenScreen
            items={items}
            today={today}
            open={openPlace}
            onOpen={setOpenPlace}
            onOpenItem={setEditing}
            query={query}
            onQuery={setQuery}
            view={kitchenView}
            onView={setKitchenView}
            toBuy={cart.filter((line) => !line.got).length}
            cart={
              <CartScreen
                lines={cart}
                suggestions={cartSuggestions}
                onAdd={addToCart}
                onToggle={(id) =>
                  writeCart(
                    cart.map((line) =>
                      line.id === id ? { ...line, got: !line.got } : line
                    )
                  )
                }
                onRemove={(id) => writeCart(cart.filter((line) => line.id !== id))}
                onClearGot={() => writeCart(cart.filter((line) => !line.got))}
                onStow={stowCheckedItems}
              />
            }
          />
        )}

        {tab === "recipes" && (
          <RecipesScreen
            items={items}
            today={today}
            onCooked={(title, used) => setCooking({ title, used })}
            onTechnique={setTechnique}
          />
        )}

        {tab === "learn" && (
          <LearnScreen practice={practice} onOpen={setTechnique} />
        )}

        {tab === "settings" && (
          <SettingsScreen onDataReplaced={() => setItems(loadFoodItems())} />
        )}
      </main>

      {undoable && (
        <Toast
          message={`Deleted ${undoable.name}`}
          duration={UNDO_MS}
          action={{ label: "Undo", onAction: undoDelete }}
          onDismiss={() => setUndoable(null)}
        />
      )}

      {stowedBatch && (
        <Toast
          message={`Moved ${stowedBatch.items.length} to the kitchen`}
          icon="check"
          duration={UNDO_MS}
          action={{ label: "Undo", onAction: undoStow }}
          onDismiss={() => setStowed(null)}
        />
      )}

      {cooked && (
        <Toast
          message={cooked.message}
          icon="check"
          duration={UNDO_MS}
          action={{ label: "Undo", onAction: undoCook }}
          onDismiss={() => setCooked(null)}
        />
      )}

      {/* Adding belongs to the kitchen, so both buttons only appear there — and
          they step aside behind a sheet, which would otherwise leave them
          dimmed under the scrim but still present. */}
      <Dock
        tab={tab}
        onSelect={(next) => withViewTransition(() => setTab(next))}
        adding={
          tab === "kitchen" && !anySheetOpen
            ? {
                onCapture: (photo) =>
                  setAdding({ category: CAMERA_CATEGORY, preset: null, photo }),
                onError: setError,
                onPick: () => setPicking(true),
              }
            : undefined
        }
      />

      {/* One layer at a time: the add sheet sits on top of its menu, and the
          edit sheet replaces everything. */}
      {cooking && (
        <CookSheet
          title={cooking.title}
          used={cooking.used}
          onConfirm={confirmCook}
          onClose={() => setCooking(null)}
        />
      )}

      {scanned && (
        <ScanReview
          found={scanned.found}
          photo={scanned.photo}
          onConfirm={confirmScan}
          onClose={() => setScanned(null)}
        />
      )}

      {receiptStatus === "working" && (
        <p className="app__reading" role="status">
          <Icon name="spinner" className="app__reading-icon" data-spinning="true" />
          Reading the receipt — this takes a few seconds.
        </p>
      )}

      {receipt && (
        <ReceiptReview
          read={receipt}
          today={today}
          onConfirm={confirmReceipt}
          onClose={() => setReceipt(null)}
        />
      )}

      {technique && (
        <TechniqueSheet
          technique={technique}
          practice={practice[technique.id]}
          onPractised={() => setPractice(loadPractice())}
          onClose={() => setTechnique(null)}
        />
      )}

      {picking && (
        <CategoryPicker
          onPick={(category) => {
            setPicking(false);
            setMenuCategory(category);
          }}
          onReceipt={(file) => void scanReceipt(file)}
          onError={setError}
          onClose={() => setPicking(false)}
        />
      )}

      {editing ? (
        <EditItemSheet
          item={editing}
          onSave={handleSave}
          onDelete={handleDelete}
          onClose={() => setEditing(null)}
        />
      ) : adding ? (
        <QuickAddItem
          category={adding.category}
          preset={adding.preset}
          initialPhoto={adding.photo ?? null}
          onAdd={handleAdd}
          onMultiple={(found) => {
            const photo = adding.photo;
            if (!photo) return;
            setAdding(null);
            setScanned({ found, photo });
          }}
          onClose={() => setAdding(null)}
        />
      ) : (
        menuCategory && (
          <CategoryMenu
            category={menuCategory}
            onPick={(preset) => setAdding({ category: menuCategory, preset })}
            onCustom={() => setAdding({ category: menuCategory, preset: null })}
            onClose={() => setMenuCategory(null)}
          />
        )
      )}
    </>
  );
}
